import { getStrapiMedia, getStrapiResponse } from "../../utils/utils.tsx";
import { useQuery } from "@tanstack/react-query";
import Page404 from "../../components/Page404/Page404.tsx";
import { useNavigate, useParams } from "react-router-dom";
import { Products_Array } from "../../types/api/product-international.ts";
import { ProductFeatureComponent_Plain } from "../../types/components/products/ProductFeatureComponent.ts";
import { ProductQuoteComponent_Plain } from "../../types/components/products/ProductQuoteComponent.ts";
import { ProductSolutionComponent_Plain } from "../../types/components/products/ProductSolutionComponent.ts";
import { ListTextComponent_Plain } from "../../types/components/shared/ListTextComponent.ts";
import { useEffect, useRef, useState } from "react";
import type { Swiper as SwiperInstance } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";
import { FreeMode, Navigation, Pagination } from "swiper/modules";
import { ProductServiceCategoryComponent_Plain } from "../../types/components/products/ProductServiceCategoryComponent.ts";
import { ProductServiceComponent_Plain } from "../../types/components/products/ProductServiceComponent.ts";
import FooterForm from "../../components/FooterForm/FooterForm.tsx";
import { updateSEO } from "../../utils/seo";
//import FooterForm from "../../components/FooterForm/FooterForm.tsx";
import "../../components/ProductPage/ProductPage.scss";
import { BlocksRenderer } from "@strapi/blocks-react-renderer";

const fetchPageData = async ({
  queryKey,
}: {
  queryKey: string[];
}): Promise<Products_Array> => {
  const response = await getStrapiResponse(queryKey[0]);
  if (!response.ok) {
    throw new Error("Network response was not ok");
  }
  return response.json();
};

const ProductPage = () => {
  const navigate = useNavigate();
  const params = useParams();
  const productUrl = `/api/product-internationals?customPopulate=nested&status=preview&filters[slug][$eq]=${
    params.slug ? params.slug : ""
  }`;

  const [activeTab, setActiveTab] = useState(0);
  // Keep SSR and the very first client render consistent to avoid hydration mismatch.
  // Real window width is applied after mount in `useEffect`.
  const [windowWidth, setWindowWidth] = useState(1024);
  const swiperRef = useRef<{ swiper: SwiperInstance } | null>(null);
  const tabsSwiperRef = useRef<{ swiper: SwiperInstance } | null>(null);
  const [isLastSlide, setIsLastSlide] = useState(false);
  const [isFirstSlide, setIsFirstSlide] = useState(true);
  let lastDirection: "next" | "prev" | null = null;
  const isTabSwitchingRef = useRef(false);

  const smartImgRef = useRef<HTMLDivElement>(null);

  const numberRefs = useRef<(HTMLDivElement | null)[]>([]);

  const featureRefs = useRef<(HTMLDivElement | null)[]>([]);

  const hoverVideoRef = useRef<HTMLVideoElement | null>(null);
  const hoverAnimFrame = useRef<number | null>(null);

  const playForward = () => {
    if (!hoverVideoRef.current) return;
    if (hoverAnimFrame.current) cancelAnimationFrame(hoverAnimFrame.current);
    hoverVideoRef.current.play();
  };

  const playReverse = () => {
    if (!hoverVideoRef.current) return;
    if (hoverAnimFrame.current) cancelAnimationFrame(hoverAnimFrame.current);
    hoverVideoRef.current.play();
  };

  useEffect(() => {
    // Update window width on mount (client-side only)
    if (typeof window !== "undefined") {
      setWindowWidth(window.innerWidth);
    }

    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const {
    data: pages,
    error,
    isLoading,
    isError,
  } = useQuery({
    queryKey: [productUrl],
    queryFn: fetchPageData,
  });

  useEffect(() => {
    updateSEO(pages?.data?.[0]?.seo); // Canonical will be set even if seo is undefined
  }, [pages]);

  useEffect(() => {
    const el = smartImgRef.current;
    if (!el || !pages?.data?.[0]) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("active");
          } else {
            entry.target.classList.remove("active");
          }
        });
      },
      {
        threshold: [0, 0.25, 0.5, 0.75, 1],
        rootMargin: "0px 0px -20% 0px",
      },
    );

    observer.observe(el);

    return () => {
      observer.disconnect();
    };
  }, [pages]);

  useEffect(() => {
    const animateNumber = (
      element: HTMLDivElement,
      endValue: number,
      duration: number,
      decimals: number = 0,
    ) => {
      let startTimestamp: number | null = null;
      const startValue = Math.max(0, endValue - (decimals > 0 ? 1 : 15));

      const step = (timestamp: number) => {
        if (!startTimestamp) startTimestamp = timestamp;
        const progress = Math.min((timestamp - startTimestamp) / duration, 1);
        const currentValue = startValue + (endValue - startValue) * progress;
        const unitMatch = element.getAttribute("data-unit") || "";
        element.textContent =
          decimals > 0
            ? currentValue.toFixed(decimals) + unitMatch
            : Math.floor(currentValue).toString() + unitMatch;

        if (progress < 1) {
          requestAnimationFrame(step);
        } else {
          const unitMatch = element.getAttribute("data-unit") || "";
          element.textContent =
            decimals > 0
              ? endValue.toFixed(decimals) + unitMatch
              : endValue.toString() + unitMatch;
        }
      };

      requestAnimationFrame(step);
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const element = entry.target as HTMLDivElement;
            const rawValue = element.getAttribute("data-num") || "0";
            const normalizedValue = rawValue.replace(",", ".");
            const endValue = parseFloat(normalizedValue) || 0;
            const decimals = normalizedValue.includes(".")
              ? normalizedValue.split(".")[1]?.length || 0
              : 0;
            const startValue = Math.max(0, endValue - (decimals > 0 ? 1 : 15));
            const unitMatch = element.getAttribute("data-unit") || "";
            element.textContent =
              decimals > 0
                ? startValue.toFixed(decimals) + unitMatch
                : startValue.toString() + unitMatch;
            animateNumber(element, endValue, 2000, decimals);
          }
        });
      },
      { threshold: 0.5 },
    );

    numberRefs.current.forEach((ref) => {
      if (ref) {
        observer.observe(ref);
      }
    });

    // Store current ref values in local variable for cleanup
    const currentRefs = numberRefs.current;

    return () => {
      currentRefs.forEach((ref) => {
        if (ref) {
          observer.unobserve(ref);
        }
      });
    };
  }, [pages]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const root = document.querySelector<HTMLElement>(".product-page-features");
    if (!root) return;

    const items = Array.from(
      root.querySelectorAll<HTMLDivElement>(".product-page-features__item"),
    );
    if (!items.length) return;

    // Initialize
    items.forEach((item) => {
      const numEl = item.querySelector<HTMLDivElement>(
        ".product-page-features__num",
      );
      const lineEl = item.querySelector<HTMLDivElement>(
        ".product-page-features__cont-line",
      );
      const title = item.querySelector<HTMLDivElement>(
        ".product-page-features__title",
      );
      const info = item.querySelector<HTMLDivElement>(
        ".product-page-features__info",
      );
      if (numEl) numEl.style.display = "none";
      if (lineEl) {
        lineEl.style.width = "0%";
      }
      if (title) title.style.opacity = "0.3";
      if (info) info.style.opacity = "0.3";
    });

    let ticking = false;
    let hasScrolled = false; // Flag to track if the user has scrolled

    // Detect initial scroll to avoid instant jump
    const handleInitialScroll = () => {
      hasScrolled = true;
      window.removeEventListener("scroll", handleInitialScroll);
    };
    window.addEventListener("scroll", handleInitialScroll, { passive: true });

    const compute = () => {
      ticking = false;
      const vh = window.innerHeight;
      const START = vh * 0.9;
      const END = vh * 0.65;

      // Find the index of the current element
      let currentIndex = -1;
      let currentProgress = 0;

      items.forEach((item, idx) => {
        const r = item.getBoundingClientRect();
        const raw = (START - r.top) / (START - END);
        const progress = Math.max(0, Math.min(1, raw));
        if (progress > 0 && progress < 1 && currentIndex === -1) {
          currentIndex = idx;
          currentProgress = progress;
        }
      });

      if (currentIndex === -1) {
        items.forEach((item, idx) => {
          const r = item.getBoundingClientRect();
          const raw = (START - r.top) / (START - END);
          if (raw >= 1) currentIndex = idx;
        });
        currentProgress = 1; // прогресс завершён
      }

      if (currentIndex === -1 && currentProgress === 0) {
        // If nothing is in progress, find the last fully completed element
        items.forEach((item, idx) => {
          const r = item.getBoundingClientRect();
          const raw = (START - r.top) / (START - END);
          if (raw >= 1) {
            currentIndex = idx;
          }
        });
      }

      items.forEach((item, idx) => {
        const numEl = item.querySelector<HTMLDivElement>(
          ".product-page-features__num",
        );
        const lineEl = item.querySelector<HTMLDivElement>(
          ".product-page-features__cont-line",
        );
        const title = item.querySelector<HTMLDivElement>(
          ".product-page-features__title",
        );
        const info = item.querySelector<HTMLDivElement>(
          ".product-page-features__info",
        );

        const effectiveProgress = hasScrolled ? currentProgress : 0;
        const isLastItemDone =
          idx === items.length - 1 && effectiveProgress >= 1;

        if (idx < currentIndex) {
          // Previous elements
          if (lineEl) lineEl.style.width = "0%";
          if (title) title.style.opacity = "1";
          if (info) info.style.opacity = "1";
          if (numEl) numEl.style.display = "none";
        } else if (idx === currentIndex) {
          // Current element
          if (isLastItemDone) {
            // Последний элемент завершил анимацию
            if (lineEl) lineEl.style.width = "0%"; // скрываем линию
            if (title) title.style.opacity = "1"; // текст полностью видим
            if (info) info.style.opacity = "1";
            if (numEl) numEl.style.display = "block";
          } else {
            // Анимация в процессе
            if (lineEl)
              lineEl.style.width = `${(effectiveProgress * 100).toFixed(3)}%`;
            const o = (0.3 + effectiveProgress * 0.7).toFixed(3);
            if (title) title.style.opacity = o;
            if (info) info.style.opacity = o;
            if (numEl)
              numEl.style.display =
                effectiveProgress > 0 && effectiveProgress < 1
                  ? "block"
                  : "none";
          }
        } else {
          // Future elements
          if (lineEl) lineEl.style.width = "0%";
          if (title) title.style.opacity = "0.3";
          if (info) info.style.opacity = "0.3";
          if (numEl) numEl.style.display = "none";
        }
      });
    };

    const onScroll = () => {
      const contRect = root.getBoundingClientRect();
      const outOfView =
        contRect.bottom < 0 || contRect.top > window.innerHeight * 1.2;
      if (outOfView) return;

      if (!ticking) {
        ticking = true;
        requestAnimationFrame(compute);
      }
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    onScroll();

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      window.removeEventListener("scroll", handleInitialScroll);
    };
  }, [pages]);

  if (isLoading) return <div className="loading-block"></div>;
  if (isError) return <div>Error: {error.message}</div>;
  if (!pages) return <div>Global error: {pages}</div>;
  if (!pages.data.length) {
    navigate("/", { replace: true });
    return <Page404 locale={""} />;
  }

  const page = pages.data[0];

  const categoryTitles = page.productServiceCategory.map(
    (category: ProductServiceCategoryComponent_Plain, index: number) => ({
      title: category.name,
      id: category.id,
      key: index,
    }),
  );
  const handleTabClick = (index: number) => {
    setActiveTab(index);
    if (swiperRef.current) {
      swiperRef.current.swiper.slideTo(0);
    }
  };

  const handleProductSlideChange = (swiper: SwiperInstance) => {
    setIsLastSlide(swiper.isEnd);
    setIsFirstSlide(swiper.isBeginning);
  };

  const handleTouchMove = (swiper: SwiperInstance) => {
    if (isTabSwitchingRef.current) return;

    lastDirection = swiper.swipeDirection;

    const slidesPerView = windowWidth >= 1024 ? 3 : windowWidth >= 768 ? 1 : 1;
    const totalSlides =
      page.productServiceCategory[activeTab]?.productServices?.length || 0;
    const isAllVisible = totalSlides <= slidesPerView;

    const goToTab = (newTabIndex: number) => {
      setActiveTab(newTabIndex);
      isTabSwitchingRef.current = true;

      setTimeout(() => {
        if (swiperRef.current) {
          swiperRef.current.swiper.slideTo(0);
        }
        isTabSwitchingRef.current = false;
      }, 150);
    };

    if (!isAllVisible) {
      if (
        isLastSlide &&
        lastDirection === "next" &&
        activeTab < categoryTitles.length - 1
      ) {
        goToTab(activeTab + 1);
      } else if (isFirstSlide && lastDirection === "prev" && activeTab > 0) {
        goToTab(activeTab - 1);
      }
    } else {
      if (lastDirection === "next" && activeTab < categoryTitles.length - 1) {
        goToTab(activeTab + 1);
      } else if (lastDirection === "prev" && activeTab > 0) {
        goToTab(activeTab - 1);
      }
    }
  };

  const wrapWords = (text: string | undefined) => {
    return (
      text
        ?.split(" ")
        .map((word, index) => <span key={index}>{word} </span>) || (
        <span key="empty">{""}</span>
      )
    );
  };

  return (
    <>
      <div className="product-page">
        <div className="product-page-main">
          <div className="product-page-main__img">
            {page.imageOrVideo ? (
              page.imageOrVideo.mime == "video/mp4" ||
              page.imageOrVideo.mime == "video/webm" ? (
                <div
                  className="slider__media"
                  dangerouslySetInnerHTML={{
                    __html: `
                  <video autoplay loop muted playsInline preload="metadata">
                    <source 
                      src="${getStrapiMedia(page.imageOrVideo.url)}" 
                      type="${page.imageOrVideo.mime}" />
                  </video>
                `,
                  }}
                />
              ) : (
                <img
                  src={getStrapiMedia(page.imageOrVideo.url)}
                  alt={page.name}
                />
              )
            ) : (
              ""
            )}
          </div>
          <div className="product-page-main__text">
            <div className="wrapper">
              <a className="back-to-all" href={"/#products"}>
                Back to all products
              </a>
              <h1 className="product-page-main__title">{page.name}</h1>
              <a className="btn-contact" href={"#contacts"}>
                Contact us
              </a>
            </div>
          </div>
        </div>
        <div className="product-page-about about-us">
          <div className="wrapper">
            <div className="about-us__left">
              <div className="block-title">About</div>
            </div>
            <div className="about-us__right">
              <div className="about-us__col">{page.about1}</div>
              <div className="about-us__col">{page.about2}</div>
            </div>
          </div>
        </div>
        <div className="product-page-smart">
          <div className="wrapper">
            <div className="product-page-smart__img" ref={smartImgRef}>
              <img
                src="../img/bg-smart-1.svg"
                alt={page.name}
                className="smart-img-1"
              />
              <img
                src="../img/bg-smart-2.svg"
                alt={page.name}
                className="smart-img-2"
              />
              <div className="product-page-smart__title">
                {wrapWords(page.smartTitle)}
              </div>
            </div>
          </div>
        </div>
        <div className="product-page-features">
          <div className="wrapper">
            <div className="product-page-features__list">
              {page.productFeatures?.map(
                (item: ProductFeatureComponent_Plain, index: number) => (
                  <div
                    key={index}
                    className="product-page-features__item"
                    ref={(el) => {
                      featureRefs.current[index] = el;
                    }}
                  >
                    <div className="product-page-features__num">
                      <span>{String(index + 1).padStart(2, "0")}</span>
                    </div>
                    <div className="product-page-features__cont">
                      <div
                        className="product-page-features__cont-line"
                        style={{ width: 0 }}
                      ></div>
                      <div
                        className="product-page-features__title"
                        style={{ opacity: 0.3 }}
                      >
                        {item.title}
                      </div>
                      <div
                        className="product-page-features__info"
                        style={{ opacity: 0.3 }}
                      >
                        {item.description}
                      </div>
                    </div>
                  </div>
                ),
              )}
            </div>
            <div className="product-page-features__btn">
              <a className="btn-black" href={"#contacts"}>
                Contact us
              </a>
            </div>
            <div className="product-page-features__decor">
              <video autoPlay loop muted playsInline>
                <source src="/img/decor.mp4" type="video/mp4" />
              </video>
            </div>
          </div>
        </div>
        <div className="product-page-quotes">
          <div className="product-page-quotes__img">
            {page.quotesBackgound ? (
              page.quotesBackgound.mime == "video/mp4" ||
              page.quotesBackgound.mime == "video/webm" ? (
                <div
                  className="slider__media"
                  dangerouslySetInnerHTML={{
                    __html: `
                  <video autoPlay loop muted playsInline preload="metadata">
                    <source 
                      src="${getStrapiMedia(page.quotesBackgound.url)}" 
                      type="${page.quotesBackgound.mime}" />
                  </video>
                `,
                  }}
                />
              ) : (
                <img
                  src={getStrapiMedia(page.quotesBackgound.url)}
                  alt={page.name}
                />
              )
            ) : (
              ""
            )}
          </div>
          <div className="wrapper">
            <div className="product-page-quotes__list">
              {page.productQuotes?.map(
                (item: ProductQuoteComponent_Plain, index: number) => (
                  <div
                    key={index}
                    className={`product-page-quotes__item  ` + item.quoteType}
                  >
                    <div className="product-page-quotes__text">
                      {item.quote}
                    </div>
                    <div className="product-page-quotes__author">
                      <div className="author__img">
                        <img src={item.authorImage?.url} />
                      </div>
                      <div className="author__inf">
                        <div className="author__name">{item.authorName}</div>
                        <div className="author__postion">
                          {item.authorPosition}
                        </div>
                      </div>
                    </div>
                  </div>
                ),
              )}
            </div>

            {page.productSolutions && page.productSolutions.length > 0 && (
              <div className="product-page-solutions">
                <div className="block-title">Solutions in Practice</div>

                <Swiper
                  modules={[Navigation, Pagination]}
                  spaceBetween={1}
                  slidesPerView={1}
                  navigation
                  pagination={{
                    clickable: true,
                    type: "fraction",
                  }}
                  autoHeight
                  className="product-page-solutions__list"
                >
                  {page.productSolutions?.map(
                    (item: ProductSolutionComponent_Plain, index: number) => (
                      <SwiperSlide
                        key={index}
                        className="product-page-solutions__item"
                      >
                        <div className="product-page-solutions__title">
                          {item.name}
                        </div>
                        <div className="product-page-solutions__cont">
                          <div className="product-page-solutions__img">
                            <img src={item.image?.url} alt={item.name} />
                          </div>
                          <div className="product-page-solutions__text">
                            <div className="product-page-solutions__info1">
                              {item.text1}
                            </div>
                            <div className="product-page-solutions__info2">
                              {item.text2}
                            </div>
                            <div className="product-page-solutions__info2">
                              {item.content ? (
                                <BlocksRenderer content={item.content} />
                              ) : (
                                ""
                              )}
                            </div>
                            <div className="product-page-solutions-number">
                              <div className="product-page-solutions-number__item">
                                <div className="product-page-solutions-number__count">
                                  {" "}
                                  {item.number}
                                </div>
                                <div className="product-page-solutions-number__text">
                                  {item.numberDescription}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </SwiperSlide>
                    ),
                  )}
                </Swiper>
              </div>
            )}
          </div>
        </div>

        <div className="product-page-services">
          <div className="wrapper">
            <div className="block-title">Our Services</div>
            <div className="tabs">
              <Swiper
                ref={tabsSwiperRef}
                modules={[FreeMode, Navigation]}
                freeMode={true}
                slidesPerView="auto"
                spaceBetween={10}
                touchRatio={1}
                navigation
                className="tabs-nav"
              >
                {categoryTitles.map((category, index) => (
                  <SwiperSlide key={category.key} style={{ width: "auto" }}>
                    <div
                      className={`tabs-nav__item ${activeTab === index ? "active" : ""}`}
                      onClick={() => handleTabClick(index)}
                      data-index={index}
                    >
                      <a href={`#${category.id}`}>{category.title}</a>
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>

              <div className="tabs-content">
                <div className="service-list">
                  <div className="service-swiper-container">
                    <Swiper
                      ref={swiperRef}
                      modules={[Navigation, Pagination]}
                      spaceBetween={16}
                      slidesPerView={4}
                      breakpoints={{
                        0: { slidesPerView: 1 },
                        768: { slidesPerView: 2 },
                        1024: { slidesPerView: 3 },
                        1200: { slidesPerView: 4 },
                      }}
                      navigation
                      pagination={{
                        clickable: true,
                        type: "fraction",
                      }}
                      touchRatio={1}
                      onSlideChange={handleProductSlideChange}
                      onTouchMove={handleTouchMove}
                      className="service-swiper"
                    >
                      {page.productServiceCategory[
                        activeTab
                      ]?.productServices.map(
                        (
                          item: ProductServiceComponent_Plain,
                          index: number,
                        ) => (
                          <SwiperSlide key={index}>
                            <div key={index} className={`service-list__item`}>
                              <div className="service-list__text">
                                <h2>{item.name}</h2>
                                <p>{item.description}</p>
                              </div>
                              <div className="service-list__top">
                                <div className="service-list__num">
                                  {`${(index + 1).toString() /*.padStart(2, "0")*/}/${
                                    page.productServiceCategory[activeTab]
                                      ?.productServices.length || 1
                                  }`}
                                </div>
                                {item.icon ? (
                                  <div className="service-list__icon">
                                    <img
                                      src={getStrapiMedia(item.icon.url)}
                                      width="40"
                                      height="40"
                                      alt={item.name}
                                    />
                                  </div>
                                ) : null}
                              </div>
                            </div>
                          </SwiperSlide>
                        ),
                      )}
                    </Swiper>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="product-page-innumbers">
          <div className="wrapper">
            <div className="product-page-innumbers__left">
              <div className="block-title">In numbers</div>
            </div>
            <div className="product-page-innumbers__right">
              <div className="product-page-innumbers__info">
                {page.inNumbersTitle}{" "}
              </div>
              <div className="product-page-innumbers__list">
                {page.numbers?.map(
                  (item: ListTextComponent_Plain, index: number) => {
                    const numberMatch = item.title
                      ? item.title.match(/[\d,.]+/)
                      : null;
                    const number = numberMatch ? numberMatch[0] : "0";
                    const unitMatch = item.title
                      ? item.title.match(/[^\d,.]+/)
                      : null;
                    const unit = unitMatch ? unitMatch[0] : "";
                    return (
                      <div
                        key={index}
                        className="product-page-innumbers__feature"
                      >
                        <div
                          className="product-page-innumbers__feature-title"
                          data-num={number}
                          data-unit={unit}
                          ref={(el) => {
                            numberRefs.current[index] = el;
                          }}
                        >
                          {item.title}
                        </div>
                        <div className="product-page-innumbers__feature-info">
                          {item.textFormat ? (
                            <BlocksRenderer content={item.textFormat} />
                          ) : (
                            item.text
                          )}
                        </div>
                      </div>
                    );
                  },
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="product-page-innumbers__img">
          <div className="wrapper">
            <div className="product-page-innumbers__img-cont">
              <img src="/img/img-numbers.svg" alt="" />
              <video
                ref={hoverVideoRef}
                muted
                playsInline
                onMouseEnter={playForward}
                onMouseLeave={playReverse}
              >
                <source src="/img/img-anim.mp4" type="video/mp4" />
              </video>
            </div>
            {/*<div className="product-page-innumbers__img-text">
              Efficient, Large-Scale Electricity Trading for Optimized Market
              Performance
            </div>*/}
          </div>
        </div>
        <div className="product-page-contacts" id="contacts">
          {page.contacts ? (
            <FooterForm data={page.contacts} office={null} locale={""} />
          ) : (
            ""
          )}
        </div>
      </div>
    </>
  );
};

export default ProductPage;
